import { useState, useEffect, useCallback } from 'react';
import { useStaggerReveal } from '@/hooks/useStaggerReveal';
import Logo from '@/components/feature/Logo';

const navLinks = [
  { label: 'Who We Are', href: '#about' },
  { label: 'How We Build', href: '#how-we-build' },
  { label: 'What We Build', href: '#what-we-build' },
  { label: 'Why AInnovation', href: '#why-ainnovation' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { ref: navRef, isVisible: navVisible } = useStaggerReveal<HTMLElement>();

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 50);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const linkClass = scrolled
    ? 'text-foreground-700 hover:text-primary-500 transition-colors duration-200'
    : 'text-white/70 hover:text-primary-400 transition-colors duration-200';

  return (
    <nav
      ref={navRef}
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-xl border-b border-background-200/60 shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a
            href="/"
            className={`flex items-center gap-0 group cursor-pointer reveal ${navVisible ? 'visible' : ''}`}
          >
            {/* Inline SVG logo — text is white, icon is green, cropped viewBox for clarity */}
            <div className="w-20 h-20 md:w-28 md:h-28 overflow-hidden flex-shrink-0 drop-shadow-[0_0_14px_rgba(132,204,22,0.55)] brightness-110">
              <Logo 
                className="w-full h-full object-contain transition-all duration-300 group-hover:brightness-125 group-hover:drop-shadow-[0_0_22px_rgba(132,204,22,0.85)]" 
                textColor={scrolled ? '#1a1a1a' : '#ffffff'}
              />
            </div>
          </a>

          <div className={`hidden md:flex items-center gap-8 stagger-reveal ${navVisible ? 'visible' : ''}`}>
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className={`text-sm font-medium whitespace-nowrap ${linkClass}`}
              >
                {link.label}
              </a>
            ))}
          </div>

          <button
            className={`md:hidden flex items-center justify-center w-10 h-10 rounded-lg cursor-pointer transition-colors duration-200 ${
              scrolled ? 'text-foreground-700 hover:bg-background-100' : 'text-white hover:bg-white/10'
            }`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl`} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`md:hidden fixed inset-0 top-16 z-40 bg-background-950/98 backdrop-blur-xl transition-transform duration-300 ${
          mobileOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col items-center gap-6 pt-10 px-6">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={() => setMobileOpen(false)}
              className="text-white/70 hover:text-white text-lg font-medium transition-colors duration-200 cursor-pointer"
            >
              {link.label}
            </a>
          ))}
          <a
            href="#contact"
            onClick={() => setMobileOpen(false)}
            className="whitespace-nowrap px-8 py-3 rounded-full text-base font-semibold bg-primary-500 text-background-950 hover:bg-primary-400 transition-all duration-200 cursor-pointer mt-4"
          >
            Start a Build
          </a>
        </div>
      </div>
    </nav>
  );
}